import React, { useState } from 'react';
import '../styles/PartnerHomeScreen.css';

const PartnerHomeScreenDemo = () => {
  // Demo partner profile data
  const [partnerProfile, setPartnerProfile] = useState({
    name: 'Alex Johnson',
    profilePicture: 'https://randomuser.me/api/portraits/men/32.jpg',
    memberSince: 'May 2025',
    rating: 4.7,
    totalRatings: 42,
    earnings: '$250.00',
    lastPayout: 'June 15, 2025'
  });

  // Demo location data
  const [location, setLocation] = useState({
    name: 'Times Square Premium Restroom',
    address: '1540 Broadway, New York, NY 10036',
    accessCode: '777',
    description: 'Clean, spacious restroom with luxury amenities in the heart of Times Square',
    photos: [],
    photoUrls: [
      'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      'https://images.unsplash.com/photo-1551524559-8af4e6624178?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      'https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
    ],
    isActive: true,
    coordinates: { lat: 40.7579, lng: -73.9855 },
    price: 2.50,
    features: ['ADA Accessible', 'Baby Changing', 'Premium Soap', 'Air Freshener'],
    reviews: [
      {
        id: 1,
        guestName: 'Sarah M.',
        guestPic: 'https://randomuser.me/api/portraits/women/44.jpg',
        rating: 5,
        comment: 'Spotless and worth every penny!',
        date: '2025-06-15'
      },
      {
        id: 2,
        guestName: 'Michael T.',
        guestPic: 'https://randomuser.me/api/portraits/men/22.jpg',
        rating: 4,
        comment: 'Great location, would be perfect with hand towels',
        date: '2025-06-10'
      },
      {
        id: 3,
        guestName: 'Jessica L.',
        guestPic: 'https://randomuser.me/api/portraits/women/68.jpg',
        rating: 5,
        comment: 'Saved me during my shopping trip! Very clean.',
        date: '2025-05-28'
      }
    ]
  });

  const [isEditing, setIsEditing] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  // Payout functions
  const handlePayout = () => setShowPayoutModal(true);
  const confirmPayout = () => {
    setPartnerProfile(prev => ({
      ...prev,
      earnings: '$0.00',
      lastPayout: new Date().toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      })
    }));
    setShowPayoutModal(false);
  };

  // Star rating renderer
  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <span key={i} className={i < rating ? 'star filled' : 'star'}>★</span>
    ));
  };

  // Review handler
  const handleAddReview = () => {
    if (newReview.comment.trim()) {
      const review = {
        id: Date.now(),
        guestName: 'Demo User',
        guestPic: 'https://randomuser.me/api/portraits/lego/1.jpg',
        rating: newReview.rating,
        comment: newReview.comment,
        date: new Date().toISOString().split('T')[0]
      };
      setLocation(prev => ({
        ...prev,
        reviews: [review, ...prev.reviews]
      }));
      setNewReview({ rating: 5, comment: '' });
    }
  };

  // Feature management
  const handleAddFeature = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      setLocation({
        ...location,
        features: [...location.features, e.target.value.trim()]
      });
      e.target.value = '';
    }
  };

  const handleRemoveFeature = (index) => {
    const newFeatures = [...location.features];
    newFeatures.splice(index, 1);
    setLocation({ ...location, features: newFeatures });
  };

  // Photo management
  const handleRemovePhoto = (index) => {
    const newUrls = [...location.photoUrls];
    newUrls.splice(index, 1);
    setLocation({ ...location, photoUrls: newUrls });
  };

  // Form toggle
  const toggleEdit = () => setIsEditing(!isEditing);

  return (
    <div className="partner-container demo-version">
      {/* Header and profile section with earnings */}
      <div className="demo-banner">
        <h2>PARTNER DASHBOARD DEMO</h2>
        <p>Showing sample Times Square location data</p>
      </div>

      <div className="partner-profile-header">
        <div className="partner-info">
          <img src={partnerProfile.profilePicture} alt="Profile" className="partner-photo"/>
          <div className="partner-details">
            <h2>{partnerProfile.name}</h2>
            <div className="partner-rating">
              {renderStars(Math.round(partnerProfile.rating))}
              <span>{partnerProfile.rating} ({partnerProfile.totalRatings} reviews)</span>
            </div>
            <p>Member since {partnerProfile.memberSince}</p>
            
            <div className="earnings-section">
              <div className="earnings-display">
                <span className="earnings-label">Earnings:</span>
                <span className="earnings-amount">{partnerProfile.earnings}</span>
              </div>
              <button 
                className="payout-button"
                onClick={handlePayout}
                disabled={partnerProfile.earnings === '$0.00'}
              >
                Payout
              </button>
            </div>
            {partnerProfile.lastPayout && (
              <p className="last-payout">Last payout: {partnerProfile.lastPayout}</p>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* FULLY EXPANDED Location Form Section */}
        <div className="location-form">
          <h2>Your Bathroom Details</h2>

          <div className="form-actions">
            {isEditing ? (
              <>
                <button className="save-button" onClick={toggleEdit}>Save Changes</button>
                <button className="cancel-button" onClick={toggleEdit}>Cancel</button>
              </>
            ) : (
              <button className="edit-button" onClick={toggleEdit}>Edit Details</button>
            )}
          </div>

          <div className="form-group">
            <label>Location Name</label>
            <input
              type="text"
              value={location.name}
              onChange={(e) => setLocation({ ...location, name: e.target.value })}
              disabled={!isEditing}
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={location.address}
              onChange={(e) => setLocation({ ...location, address: e.target.value })}
              disabled={!isEditing}
            />
          </div>

          <div className="form-group">
            <label>Access Code</label>
            <input
              type="text"
              value={location.accessCode}
              onChange={(e) => setLocation({ ...location, accessCode: e.target.value })}
              disabled={!isEditing}
            />
          </div>

          <div className="form-group">
            <label>Price per Visit</label>
            <div className="price-input">
              <span>$</span>
              <input
                type="number"
                min="0"
                step="0.50"
                value={location.price}
                onChange={(e) => setLocation({ ...location, price: parseFloat(e.target.value) })}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Features</label>
            <div className="features-tags">
              {location.features.map((feature, index) => (
                <span key={index} className="feature-tag">
                  {feature}
                  {isEditing && (
                    <button onClick={() => handleRemoveFeature(index)}>×</button>
                  )}
                </span>
              ))}
              {isEditing && (
                <input
                  type="text"
                  placeholder="Add feature"
                  onKeyDown={handleAddFeature}
                />
              )}
            </div>
          </div>

          <div className="photo-upload">
            <label>Photos</label>
            <div className="photo-preview">
              {location.photoUrls.map((url, index) => (
                <div key={index} className="photo-thumbnail">
                  <img src={url} alt={`Bathroom ${index + 1}`} />
                  {isEditing && (
                    <button onClick={() => handleRemovePhoto(index)}>×</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="visibility-toggle">
            <label>
              Make My Bathroom Visible to Guests
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={location.isActive}
                  onChange={() => setLocation({ ...location, isActive: !location.isActive })}
                />
                <span className="slider"></span>
              </div>
            </label>
            <p className="toggle-hint">
              {location.isActive
                ? 'Your bathroom appears in search results'
                : 'Your bathroom is hidden from guests'}
            </p>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="reviews-section">
          <h2>Customer Reviews</h2>
          <div className="average-rating">
            <div className="stars-large">
              {renderStars(Math.round(location.reviews.reduce((acc, r) => acc + r.rating, 0) / location.reviews.length))}
            </div>
            <span>
              {location.reviews.length} reviews • 
              {(location.reviews.reduce((acc, r) => acc + r.rating, 0) / location.reviews.length).toFixed(1)} average
            </span>
          </div>

          <div className="add-review">
            <h3>Add a Test Review</h3>
            <div className="star-rating-input">
              {[1, 2, 3, 4, 5].map((star) => (
                <span 
                  key={star} 
                  className={star <= newReview.rating ? 'star filled' : 'star'}
                  onClick={() => setNewReview({ ...newReview, rating: star })}
                >
                  ★
                </span>
              ))}
            </div>
            <textarea
              placeholder="Write your review..."
              value={newReview.comment}
              onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
            />
            <button onClick={handleAddReview}>Submit Review</button>
          </div>

          <div className="reviews-list">
            {location.reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <img src={review.guestPic} alt={review.guestName} className="guest-photo" />
                  <div>
                    <h4>{review.guestName}</h4>
                    <div className="review-rating">
                      {renderStars(review.rating)}
                      <span className="review-date">{review.date}</span>
                    </div>
                  </div>
                </div>
                <p className="review-comment">{review.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="payout-modal">
          <div className="modal-content">
            <h3>Confirm Payout</h3>
            <p>You are about to transfer {partnerProfile.earnings} to your bank account.</p>
            <div className="modal-actions">
              <button className="confirm-button" onClick={confirmPayout}>
                Confirm Transfer
              </button>
              <button className="cancel-button" onClick={() => setShowPayoutModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerHomeScreenDemo;